import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import {
	ArrowTopRightOnSquareIcon,
	BanknotesIcon,
	CalendarDaysIcon,
	DocumentTextIcon,
	ShieldCheckIcon,
	PlusCircleIcon,
} from '@heroicons/react/24/outline'

import InvoicesService from '@/services/InvoicesService'

const CURRENCY_SYMBOL = 'PLN'
const LOAD_FAILED_KEY = 'customers.details.invoices.loadFailed'

const dec = value => {
	if (value == null) return 0
	if (typeof value === 'object' && value.$numberDecimal != null) {
		const n = Number(value.$numberDecimal)
		return Number.isFinite(n) ? n : 0
	}
	const asNumber = Number(value)
	return Number.isFinite(asNumber) ? asNumber : 0
}

const fmtMoney = amount => `${CURRENCY_SYMBOL} ${dec(amount).toFixed(2)}`
const fmtDate = (date, pattern = 'YYYY-MM-DD') => (date ? moment(date).format(pattern) : '')
const fmtStatus = status => (status ? String(status).toLowerCase() : 'pending')

const badgeClass = status => {
	switch (fmtStatus(status)) {
		case 'paid':
			return 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-100'
		case 'overdue':
			return 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-100'
		default:
			return 'bg-slate-50 text-slate-700 ring-1 ring-inset ring-slate-200'
	}
}

export default function CustomerInvoices({
	customerId,
	title = 'Invoices',
	listPath = '/ksiegowosc/invoices',
	addPath = '/ksiegowosc/invoices/add',
	detailsBasePath = '/ksiegowosc/invoices/details',
}) {
	const { t } = useTranslation()
	const normalizedCustomerId = useMemo(() => String(customerId ?? '').trim(), [customerId])
	const hasCustomer = Boolean(normalizedCustomerId)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [items, setItems] = useState([])

	const loadInvoices = useCallback(async () => {
		if (!normalizedCustomerId) {
			setItems([])
			setError(null)
			return
		}

		setLoading(true)
		setError(null)
		try {
			const response = await InvoicesService.getInvoicesByCustomer(normalizedCustomerId)
			const entries = Array.isArray(response?.data) ? response.data : []
			setItems(entries)
		} catch (err) {
			setError(err?.message || LOAD_FAILED_KEY)
		} finally {
			setLoading(false)
		}
	}, [normalizedCustomerId])

	useEffect(() => {
		loadInvoices()
	}, [loadInvoices])

	const addInvoiceLink = useMemo(() => {
		if (!hasCustomer) return addPath
		return `${addPath}?customerId=${encodeURIComponent(normalizedCustomerId)}`
	}, [addPath, hasCustomer, normalizedCustomerId])

	return (
		<li className="col-span-1">
			<div className="overflow-hidden rounded-md border border-slate-200 bg-gradient-to-b from-slate-50 to-white shadow-sm">

				{/* Header */ }
				<div className="flex flex-row gap-3 border-b border-slate-300 px-6 py-3 sm:items-center sm:justify-between mb-4 bg-gray-100">
					<div className="flex items-start gap-3">
						<div className="flex flex-col gap-1">
							<h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
								<DocumentTextIcon className="h-5 w-5 text-sky-700" />
								<span>{title}</span>
							</h3>
							<p className="hidden sm:block text-sm leading-6 text-slate-700">{t('customers.details.invoices.subtitle')}</p>
						</div>
					</div>
					<div className="ml-auto flex items-center gap-4">
						<Link
							to={listPath}
							className="inline-flex items-center gap-2 text-sm text-sky-700 hover:text-sky-800">
							<ArrowTopRightOnSquareIcon className="h-5 w-5" />
							<span>{t('customers.details.invoices.goToList')}</span>
						</Link>
						<Link
							to={addInvoiceLink}
							className={`inline-flex items-center rounded-md bg-sky-600 px-3 py-2 text-xs text-white shadow-sm transition hover:bg-sky-700`}
							onClick={evt => {if (!hasCustomer) evt.preventDefault()}}>
							<span>{t('customers.details.invoices.add')}</span>
						</Link>
					</div>
				</div>

			    {/* Invoices table */ }
				<div className="px-4 pb-4 sm:px-6 sm:pb-6">
					{!hasCustomer ? (
						<div className="flex items-center gap-3 rounded-md border border-dashed border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
							<ShieldCheckIcon className="h-5 w-5 text-slate-400" />
							<span>{t('customers.details.invoices.selectPrompt')}</span>
						</div>
					) : loading ? (
						<div className="rounded-md border border-slate-200 bg-white px-4 py-4 text-sm text-slate-600">{t('customers.details.invoices.loading')}</div>
					) : error ? (
						<div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">{t(error, { defaultValue: error })}</div>
					) : items.length === 0 ? (
						<div className="flex min-h-[50px] items-center justify-center gap-3 rounded-md bg-white px-4 pt-5 text-sm text-slate-600">
							<DocumentTextIcon className="h-5 w-5 text-slate-400" />
							<span>{t('customers.details.invoices.noData')}</span>
						</div>
					) : (
						<div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
							<div className="max-h-64 overflow-x-auto">
								<table className="min-w-full text-sm text-slate-800">
									<thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
										<tr>
											<th className="px-4 py-3 text-left">
												<div className="flex items-center gap-2">
													<CalendarDaysIcon className="h-4 w-4" />
													<span>{t('customers.details.invoices.date')}</span>
												</div>
											</th>
											<th className="px-4 py-3 text-left">
												<div className="flex items-center gap-2">
													<DocumentTextIcon className="h-4 w-4" />
													<span>{t('customers.details.invoices.invoiceNumber')}</span>
												</div>
											</th>
											<th className="px-4 py-3 text-left">
												<div className="flex items-center gap-2">
													<ShieldCheckIcon className="h-4 w-4" />
													<span>{t('customers.details.invoices.status')}</span>
												</div>
											</th>
											<th className="px-4 py-3 text-left">
												<div className="flex items-center gap-2">
													<BanknotesIcon className="h-4 w-4" />
													<span>{t('customers.details.invoices.total')}</span>
												</div>
											</th>
											<th className="px-4 py-3 text-right" />
										</tr>
									</thead>
									<tbody className="divide-y divide-slate-100">
											{items.map((item, index) => {
												const rowKey = item._id ?? item.number ?? index
												const linkTarget = item._id ? `${detailsBasePath}/${item._id}` : listPath
												const statusValue = fmtStatus(item.status || item.invoiceStatus)
												const statusLabel = t(`customers.details.invoices.statusOptions.${statusValue}`, {
													defaultValue: statusValue ? `${statusValue.charAt(0).toUpperCase()}${statusValue.slice(1)}` : t('customers.details.invoices.pending'),
												})
												const date = item.date || item.created
											return (
												<tr key={rowKey} className="bg-white transition-colors hover:bg-slate-50/80">
														<td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-slate-900">{fmtDate(date) || t('customers.details.invoices.na')}</td>
													<td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-slate-800">
														<span className="font-mono">{item.number || t('customers.details.invoices.na')}</span>
													</td>
													<td className="whitespace-nowrap px-4 py-3">
														<span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${badgeClass(statusValue)}`}>
															<ShieldCheckIcon className="h-4 w-4" />
															{statusLabel}
														</span>
													</td>
													<td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-slate-900">{fmtMoney(item.total)}</td>
													<td className="whitespace-nowrap px-4 py-3 text-right">
														<Link
															to={linkTarget}
															className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-3 py-2 text-xs text-white shadow-sm transition hover:bg-sky-700">
															<span>{t('customers.details.invoices.view')}</span>
														</Link>
													</td>
												</tr>
											)
										})}
									</tbody>
								</table>
							</div>
						</div>
					)}
				</div>
			</div>
		</li>
	)
}
